import * as constants from "../constants/CONSTANT";
import axios from "axios";

class GoogleCalendarApi {
  constructor() {
    this.baseURL = `${constants.API_BASE_URL}/api/google-calendar`;

    // Create axios instance with interceptor for automatic token injection
    this.axiosInstance = axios.create({
      baseURL: this.baseURL
    });

    // Request interceptor to automatically add JWT token and Google tokens
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add JWT token for user authentication
        const token = localStorage.getItem('token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Add Google access token and refresh token from localStorage
        const googleAccessToken = localStorage.getItem('googleAccessToken');
        const googleRefreshToken = localStorage.getItem('googleRefreshToken');
        
        if (googleAccessToken) {
          config.headers['x-google-access-token'] = googleAccessToken;
        }
        
        if (googleRefreshToken) {
          config.headers['x-google-refresh-token'] = googleRefreshToken;
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get authorization URL for Google OAuth
   */
  async getAuthUrl() {
    try {
      // JWT token automatically added by interceptor
      const response = await this.axiosInstance.get('/auth/url');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handle OAuth callback and exchange code for token
   */
  async handleCallback(code) {
    try {
      // JWT token automatically added by interceptor
      const response = await this.axiosInstance.post('/auth/callback', { code });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all calendars from Google
   */
  async getAllCalendars() {
    try {
      const response = await this.axiosInstance.get('/calendars');
      return response.data.calendars || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get calendar events from Google
   * @param {string} calendarId - Google Calendar ID (default: 'primary')
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  async getCalendarEvents(calendarId = 'primary', startDate, endDate) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      // ✅ Add cache-busting timestamp to force fresh data
      params.append('_t', Date.now());

      const response = await this.axiosInstance.get(
        `/events/${calendarId}?${params.toString()}`,
        {
          // ✅ Disable axios caching
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      return response.data.events || response.data.items || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new event in Google Calendar
   * @param {Object} eventData - Event data
   */
  async createEvent(eventData) {
    try {
      const response = await this.axiosInstance.post('/events', eventData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update an existing event in Google Calendar
   * @param {string} eventId - Google Event ID
   * @param {Object} eventData - Updated event data
   */
  async updateEvent(eventId, eventData) {
    try {
      const response = await this.axiosInstance.put(`/events/${eventId}`, eventData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete an event from Google Calendar
   * @param {string} eventId - Google Event ID
   */
  async deleteEvent(eventId) {
    try {
      const response = await this.axiosInstance.delete(`/events/${eventId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user profile information
   */
  async getUserInfo() {
    try {
      const response = await this.axiosInstance.get('/user/info');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Test Google Calendar connection
   */
  async testConnection() {
    try {
      const response = await this.axiosInstance.get('/test');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sync calendar with Google
   */
  async syncWithGoogle() {
    try {
      const response = await this.axiosInstance.post('/sync', {});
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Revoke Google Calendar access
   */
  async revokeAccess() {
    try {
      const response = await this.axiosInstance.post('/auth/revoke', {});
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Store refresh token and access token in database
   */
  async storeTokens(refreshToken, accessToken) {
    try {
      const response = await this.axiosInstance.post('/store-tokens', {
        refresh_token: refreshToken,
        access_token: accessToken
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

const googleCalendarApiInstance = new GoogleCalendarApi();
export default googleCalendarApiInstance;
